import cx from "classnames";
import { useCallback, useId, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { Field, Form } from "react-final-form";
import { Label, Col, FormText, Row, Card, CardBody, Badge } from "reactstrap";
import { DateInput } from "../../components/inputs/final_form/DateInput";
import { genderOptions } from "../../components/inputs/GenderSelector";
import { PayorSelector } from "../../components/inputs/final_form/PayorSelector";
import { SelectInput } from "../../components/inputs/final_form/SelectInput";
import { TestSelector } from "../../components/inputs/final_form/TestSelector";
import { CptSelector } from "../../components/inputs/final_form/CptSelector";
import { Icd10Selector } from "../../components/inputs/final_form/Icd10Selector";
import { camelizeKeys } from "../../util/helpers";
import { LoadingButton } from "../../components/LoadingButton";
import { TextInput } from "../../components/inputs/final_form/TextInput";
import { DetailedErrorAlert } from "../../components/DetailedErrorAlert";
import { useDebounced } from "../../util/hooks";
import { isMemberIdInvalid } from "../../workflow/member_id_input";
import { MissingInformationDropdown } from "../../components/MissingInformationDropdown";
import { BoolIcon } from "../../components/BoolIcon";
import { Check, Info } from "lucide-react";
import { formatDate } from "../../util/formatters";
import { isEmpty, get, isArray } from "lodash-es";
import { OnChange } from "react-final-form-listeners";
import { FORM_ERROR } from "final-form";
import { api, redirectTo } from "../../util/api";

const FieldRow = ({ label, currentValue, name, component, placeholder, className, validate, children, missing }) => {
  const [provided, setProvided] = useState(false);

  const id = useId();

  const handleDataInput = (values) => {
    if (values && !isArray(values)) {
      return setProvided(true);
    }

    if (values && isArray(values) && values?.length > 0) {
      return setProvided(true);
    }

    return setProvided(false);
  };

  return (
    <Row className={cx("mb-1", className)}>
      <Col xs="2">
        <Label for={id} sm={2} className="fw-bold w-auto">
          {label}
        </Label>
      </Col>

      <Col xs="5" className="pt-1">
        <Row>
          <p className="mb-0 align-self-center">{currentValue}</p>
        </Row>

        <Row>{children}</Row>

        <Row>
          {provided && !currentValue && (
            <FormText color="muted">
              Filled <Check size={16} className="text-success" />
            </FormText>
          )}
        </Row>
      </Col>

      <Col xs="5">
        {missing && !provided && (
          <FormText className="mt-0" color="muted">
            Missing field.
          </FormText>
        )}

        <Field placeholder={placeholder} component={component} name={name} id={id} validate={validate} className="mb-3" isClearable />
        <OnChange name={name}>{handleDataInput}</OnChange>
      </Col>
    </Row>
  );
};

const useTestSelectorForLab = (labId) => {
  return useMemo(() => (props) => <TestSelector isMulti labIds={labId} {...props} />, [labId]);
};

const npiValidator = (npi) => {
  if (npi && npi.length < 9) {
    return "Need valid NPI Number";
  }

  return undefined;
};

const clearedOrNotProvided = (object, path) => {
  return get(object, path)?.length === 0 || !get(object, path);
};

const EditVerifiedFieldsForm = ({
  labId,
  currentValues,
  editVerifiedFieldsPath,
  substatuses,
  updateRequestStatusPath,
  preprocessingCategories,
}) => {
  const {
    firstName,
    lastName,
    dob,
    gender,
    memberId,
    payorName,
    verifiedTestNames,
    verifiedIcd10Codes,
    verifiedCptCodes,
    enrolled: providerEnrolled,
    npi,
    verifiedDos,
    closed,
  } = currentValues;

  const [error, setError] = useState();

  const invalidMemberIdValidator = useDebounced(
    useCallback(async (value) => {
      const isInvalid = await isMemberIdInvalid(value);
      return isInvalid ? "This matches an invalid member ID" : undefined;
    }, []),
    300,
    true
  );

  const missingFieldsMap = {
    firstName: {
      isEmpty: isEmpty(firstName),
      path: "request_patient.first_name",
    },
    lastName: {
      isEmpty: isEmpty(lastName),
      path: "request_patient.last_name",
    },
    dob: {
      isEmpty: isEmpty(dob),
      path: "request_patient.dob",
    },
    gender: {
      isEmpty: isEmpty(gender),
      path: "request_patient.gender",
    },
    memberId: {
      isEmpty: isEmpty(memberId),
      path: "case.primary_request_insurance.memberid",
    },
    payorName: {
      isEmpty: isEmpty(payorName),
      path: "case.payor_id",
    },
    verifiedDos: {
      isEmpty: isEmpty(verifiedDos),
      path: "case.pa_request_attributes.fixed_verified_dos",
    },
    verifiedTestNames: {
      isEmpty: isEmpty(verifiedTestNames),
      path: "case.verified_test_ids",
    },
    verifiedIcd10Codes: {
      isEmpty: isEmpty(verifiedIcd10Codes),
      path: "case.verified_icd10_codes",
    },
    verifiedCptCodes: {
      isEmpty: isEmpty(verifiedCptCodes),
      path: "case.verified_cpt_codes",
    },
    npi: {
      isEmpty: isEmpty(npi),
      path: "request_provider.npi",
    },
  };

  const handleKaseFix = useCallback(
    async (values) => {
      try {
        const params = {
          ...values,
          fix: true,
        };

        const res = await api.post(editVerifiedFieldsPath, params);

        const { redirect_path, message } = res.data;

        if (redirect_path && message) {
          redirectTo(redirect_path, message, "success");
        } else {
          redirectTo("/workflow", "Updated case fields.", "success");
        }
      } catch (err) {
        setError(err);
      }
    },
    [editVerifiedFieldsPath]
  );

  const handleSaveProgress = useCallback(
    async (values) => {
      try {
        const res = await api.post(editVerifiedFieldsPath, values);

        const { redirect_path, message } = res.data;

        if (redirect_path && message) {
          redirectTo(redirect_path, message, "success");
        } else {
          window.location.reload();
        }
      } catch (err) {
        setError(err);
      }
    },
    [editVerifiedFieldsPath]
  );

  const LabTestSelector = useTestSelectorForLab(labId);

  const verifiedFieldsValidator = (values) => {
    const missingFields = [];

    Object.keys(missingFieldsMap).map((key) => {
      if (missingFieldsMap[key]?.isEmpty && clearedOrNotProvided(values, missingFieldsMap[key].path)) {
        missingFields.push(key);
      }
    });

    if (missingFields?.length === 0) return undefined;

    return { [FORM_ERROR]: "Please, ensure that every Missing Field is provided." };
  };

  return (
    <>
      {preprocessingCategories && (
        <div className="mb-3">
          <div className="d-flex flex-row gap-2 align-items-center">
            <div className="d-flex flex-column">
              <h6 className="fw-semibold mb-0">Preprocessing Categories:</h6>
            </div>

            <div className="d-flex flex-column">
              <div className="d-flex flex-row gap-2">
                {preprocessingCategories.length > 0 ? (
                  <>
                    {preprocessingCategories.map((category) => (
                      <Badge key={category.id} color={category.active ? "primary" : "secondary"}>
                        {category.name}
                      </Badge>
                    ))}
                  </>
                ) : (
                  <p className="mb-0">None.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <DetailedErrorAlert error={error} />
      <Form onSubmit={handleKaseFix} validate={verifiedFieldsValidator}>
        {({ handleSubmit, form, valid, submitFailed, submitting, pristine, values }) => (
          <>
            <FieldRow
              label="First Name"
              placeholder="First Name"
              component={TextInput}
              name={"request_patient[first_name]"}
              currentValue={firstName}
              missing={isEmpty(firstName)}
            />

            <FieldRow
              label="Last Name"
              placeholder="Last Name"
              component={TextInput}
              name={"request_patient[last_name]"}
              currentValue={lastName}
              missing={isEmpty(lastName)}
            />

            <FieldRow
              label="Date of Birth"
              placeholder="MM/DD/YYYY"
              component={DateInput}
              name={"request_patient[dob]"}
              currentValue={formatDate(dob)}
              missing={isEmpty(dob)}
            />

            <FieldRow
              label="Gender"
              placeholder="Select a Gender"
              component={(props) => <SelectInput options={genderOptions} {...props} />}
              name={"request_patient[gender]"}
              currentValue={gender}
              missing={isEmpty(gender)}
            />

            <FieldRow
              label="Member ID"
              component={TextInput}
              name={"primary_request_insurance[memberid]"}
              currentValue={memberId}
              placeholder="Member ID"
              validate={invalidMemberIdValidator}
              missing={isEmpty(memberId)}
            />

            <FieldRow
              label="Provider NPI"
              component={TextInput}
              name={"request_provider[npi]"}
              type="number"
              placeholder="NPI Number"
              currentValue={npi}
              className="align-items-start"
              validate={npiValidator}
              missing={isEmpty(npi)}
            >
              {!isEmpty(npi) && (
                <FormText>
                  Enrolled:
                  <BoolIcon className="ms-1" id="enrollment_icon" size={16} value={providerEnrolled} />
                </FormText>
              )}
            </FieldRow>

            <FieldRow
              label="Payor"
              placeholder="Select a Payor..."
              component={PayorSelector}
              name={"case[payor_id]"}
              currentValue={payorName}
              missing={isEmpty(payorName)}
            />

            <FieldRow
              label="Date of Service"
              placeholder="MM/DD/YYYY"
              component={DateInput}
              name={"case[pa_request_attributes][fixed_verified_dos]"}
              currentValue={formatDate(verifiedDos)}
              missing={isEmpty(verifiedDos)}
            />

            <FieldRow
              label="Test"
              placeholder="Select one or more Tests..."
              component={LabTestSelector}
              name={"case[verified_test_ids]"}
              currentValue={verifiedTestNames.join(", ")}
              missing={isEmpty(verifiedTestNames)}
            />

            <FieldRow
              label="ICD 10 Codes"
              placeholder="Select one or more ICD 10 Codes..."
              component={Icd10Selector}
              name={"case[verified_icd10_codes]"}
              currentValue={verifiedIcd10Codes.join(", ")}
              missing={isEmpty(verifiedIcd10Codes)}
            />

            <FieldRow
              label="CPT Codes"
              placeholder="Select one or more CPT Codes..."
              component={CptSelector}
              name={"case[verified_cpt_codes]"}
              currentValue={verifiedCptCodes.join(", ")}
              className="align-items-start"
              missing={isEmpty(verifiedCptCodes)}
            />

            <Card className="mb-3 border-0 bg-info bg-opacity-10">
              <CardBody>
                <div className="d-flex flex-row gap-2">
                  <Info size={28} />

                  <p className="mb-0 align-self-center">
                    Fixing the Case will return it to Received status. Saving your progress will store the data that you currently have and
                    leave the case in the same status.
                  </p>
                </div>
              </CardBody>
            </Card>

            <div className="d-flex flex-row justify-content-between">
              <div className="d-flex flex-col">
                {!closed && substatuses && (
                  <MissingInformationDropdown substatuses={substatuses} updateRequestStatusPath={updateRequestStatusPath} />
                )}
              </div>

              <div className="d-flex flex-col">
                <LoadingButton
                  loading={submitting}
                  type="button"
                  disabled={!valid || pristine}
                  color="primary"
                  onClick={handleSubmit}
                  title="Fix all Case information"
                  className="me-2"
                >
                  {closed ? "Fix and reopen" : "Fix Case"}
                </LoadingButton>

                <LoadingButton
                  loading={submitting}
                  type="button"
                  disabled={pristine || closed}
                  color="secondary"
                  onClick={() => handleSaveProgress(values)}
                  title="Save the fields I have"
                >
                  Save progress
                </LoadingButton>
              </div>
            </div>
          </>
        )}
      </Form>
    </>
  );
};

export const initEditVerifiedFields = () => {
  const editVerifiedFieldsContainer = document.getElementById("edit-verified-fields-page");

  if (editVerifiedFieldsContainer) {
    const root = createRoot(editVerifiedFieldsContainer);

    const {
      case: caseData,
      editVerifiedFieldsPath,
      missingInfoSubstatuses,
      updateRequestStatusPath,
      preprocessingCategories,
    } = editVerifiedFieldsContainer.dataset;

    const kase = camelizeKeys(JSON.parse(caseData));
    const substatuses = JSON.parse(missingInfoSubstatuses);
    const parsedPreprocessingCategories = JSON.parse(preprocessingCategories);

    root.render(
      <EditVerifiedFieldsForm
        labId={kase.labInfo.id}
        currentValues={kase}
        editVerifiedFieldsPath={editVerifiedFieldsPath}
        substatuses={substatuses}
        updateRequestStatusPath={updateRequestStatusPath}
        preprocessingCategories={parsedPreprocessingCategories}
      />
    );
  }
};
